import { useState, type ReactNode } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  LockKeyhole,
  MessageSquareText,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { DashboardContainer } from "../components/dashboard-container";
import { GuidanceDisclosure } from "../components/guidance-disclosure";
import { useStudioStore } from "@/store/studio-store";
import { cogoXyzRegistry } from "@/features/cogo-xyz/cogo-xyz-registry";
import { pushStudioRoute } from "@/lib/studio-navigation";
import type { ChatMessage } from "@/features/channel-router";
import { useStudioLocale } from "@/i18n/use-studio-locale";
import type { StudioDictionary } from "@/i18n/studio-dictionaries";

// Icon and tone stay here; the words come from the dictionary so the
// explainer reads in the reviewer's language like the rest of the screen.
const rows = [
  {
    icon: ClipboardList,
    key: "inProgress",
    tone: "border border-border bg-muted text-muted-foreground",
  },
  {
    icon: ShieldCheck,
    key: "needsApproval",
    tone: "border border-border bg-muted text-muted-foreground",
  },
  {
    icon: CheckCircle2,
    key: "reviewTrail",
    tone: "border border-border bg-muted text-muted-foreground",
  },
  {
    icon: LockKeyhole,
    key: "accessControl",
    tone: "bg-muted text-muted-foreground",
  },
] as const satisfies ReadonlyArray<{
  icon: typeof ClipboardList;
  key: keyof StudioDictionary["requests"]["rows"];
  tone: string;
}>;

type AgentWorklogMessage = Extract<ChatMessage, { kind: "agent-worklog" }>;
type ToolRun = ReturnType<typeof cogoXyzRegistry.snapshot>["toolRuns"][number];

type RequestsCopy = StudioDictionary["requests"];

const INLINE_APPROVAL_COPY = {
  en: {
    title: "Approvals stay in the conversation",
    body: "Approve or decline a pending action where it was requested in Project Chat or Bot. This page is the durable history and recovery view, not a separate approval workspace.",
  },
  ko: {
    title: "승인은 대화 안에서 처리합니다",
    body: "대기 중인 작업은 요청이 만들어진 프로젝트 채팅 또는 봇에서 승인하거나 거절합니다. 이 페이지는 별도 승인 공간이 아니라 지속 기록과 복구 화면입니다.",
  },
  ja: {
    title: "承認は会話内で行います",
    body: "保留中の操作は、依頼が作成されたプロジェクトチャットまたはボットで承認・拒否します。このページは別の承認ワークスペースではなく、永続履歴と復旧のための画面です。",
  },
  zh: {
    title: "审批在对话中完成",
    body: "请在发起请求的项目对话或机器人中批准或拒绝待处理操作。此页面用于持久记录和恢复，不是独立的审批工作区。",
  },
  "zh-TW": {
    title: "核准在對話中完成",
    body: "請在發出請求的專案對話或機器人中核准或拒絕待處理操作。此頁面用於持久記錄與復原，不是獨立的核准工作區。",
  },
} as const;

function requestTitle(toolName: string, copy: RequestsCopy): string {
  if (toolName.includes("create_cogo_app")) return copy.requestTitle.appDraft;
  if (toolName.includes("import_cogo_asset")) return copy.requestTitle.sourceAdded;
  if (toolName.includes("preview") || toolName.includes("review"))
    return copy.requestTitle.reviewCheck;
  if (toolName.includes("publish") || toolName.includes("gallery"))
    return copy.requestTitle.shareUpdate;
  if (toolName.includes("deploy")) return copy.requestTitle.launchSetup;
  return copy.requestTitle.requestedChange;
}

function statusLabel(status: string, copy: RequestsCopy): string {
  if (/approval|waiting/i.test(status)) return copy.tiles.needsApproval;
  if (/pass|success|succeed|complete|done/i.test(status))
    return copy.status.ready;
  if (/fail|error|blocked/i.test(status)) return copy.status.needsReview;
  if (/pending|running|active/i.test(status)) return copy.status.inProgress;
  return copy.status.recorded;
}

function scopeLabel(scope: string, copy: RequestsCopy): string {
  if (scope.startsWith("github:")) return copy.scope.codeUpdate;
  if (scope.startsWith("figma:")) return copy.scope.designFileUpdate;
  if (scope === "cogo:publish_gallery") return copy.scope.publicShare;
  if (scope === "deploy:create") return copy.scope.launchSetup;
  return copy.scope.workspaceUpdate;
}

function entryLabel(label: string, copy: RequestsCopy): string {
  if (label === "reuse") return copy.entry.reuse;
  if (label === "fresh") return copy.entry.fresh;
  if (label === "validation") return copy.entry.validation;
  if (label === "approval") return copy.entry.approval;
  return label;
}

function requiresApproval(run: ToolRun): boolean {
  const outputStatus = run.output?.status;
  return (
    run.status === ("approval_required" as ToolRun["status"]) ||
    outputStatus === "approval_required"
  );
}

interface TimelineItemShellProps {
  title: string;
  status: string;
  timestamp: string | number;
  children: ReactNode;
}

function TimelineItemShell({
  title,
  status,
  timestamp,
  children,
}: TimelineItemShellProps) {
  return (
    <details className="group rounded-md border border-border/70 bg-background open:border-border">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-3 marker:hidden">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-foreground">
            {title}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {new Date(timestamp).toLocaleString("ko-KR")}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
            {status}
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
        </div>
      </summary>
      <div className="border-t border-border/70 px-3 pb-3 pt-3">
        {children}
      </div>
    </details>
  );
}

export function AgentRunsPage() {
  const { locale, strings } = useStudioLocale();
  const requestsCopy = strings.requests;
  const inlineApprovalCopy = INLINE_APPROVAL_COPY[locale];
  const flowCopy = strings.businessScreens.requestFlow;
  const chatSidebarOpen = useStudioStore((s) => s.chatSidebarOpen);
  const toggleChatSidebar = useStudioStore((s) => s.toggleChatSidebar);
  const chatComposerDraft = useStudioStore((s) => s.chatComposerDraft);
  const setChatContext = useStudioStore((s) => s.setChatContext);
  const setChatComposerDraft = useStudioStore((s) => s.setChatComposerDraft);
  const chatMessages = useStudioStore((s) => s.chatMessages);
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute);
  const [, setRegistryVersion] = useState(0);
  const cogoXyzSnapshot = cogoXyzRegistry.snapshot();
  const requestUpdates = chatMessages
    .filter(
      (message): message is AgentWorklogMessage =>
        message.kind === "agent-worklog",
    )
    .slice(-5)
    .reverse();
  const approvalRequests = cogoXyzSnapshot.toolRuns.filter(requiresApproval);
  const recentRequests = cogoXyzSnapshot.toolRuns.slice(-6).reverse();
  const recentRequestIds = new Set(recentRequests.map((run) => run.id));
  const visibleRequests = [
    ...approvalRequests.filter((run) => !recentRequestIds.has(run.id)).reverse(),
    ...recentRequests,
  ];
  const hasRequests = requestUpdates.length > 0 || recentRequests.length > 0;

  function navigateTo(route: "import" | "library" | "apps-live") {
    setStudioRoute(route);
    pushStudioRoute(route);
  }

  function openChat() {
    if (!chatSidebarOpen) toggleChatSidebar();
  }

  function openRequestsChat() {
    const visibleStatus = [
      `${requestsCopy.tiles.updates} ${requestUpdates.length}`,
      `${requestsCopy.tiles.checks} ${recentRequests.length}`,
      `${requestsCopy.tiles.needsApproval} ${approvalRequests.length}`,
    ].join(" · ");
    setChatContext({
      mode: "chat",
      surfaceId: "requests-review",
      operationLabel: `${requestsCopy.title} · ${visibleStatus}`,
      operationKind: "requests-review",
      chatOperationInstructions: [
        "The user selected the current Requests & Reviews surface.",
        `Visible state: updates=${requestUpdates.length}; checks=${recentRequests.length}; approvals=${approvalRequests.length}; empty=${!hasRequests}.`,
        "Answer read-only status questions from this visible state. Do not propose or execute an application mutation unless the user explicitly requests one.",
      ].join("\n"),
    });
    openChat();
  }

  function openChatWithContext(params: {
    id: string;
    title: string;
    status: string;
    kind: string;
    requestedAt: string | number;
    workspaceId?: string;
    permissionScope?: readonly string[];
  }) {
    const contextLine = `${requestsCopy.openChat}: ${params.title} · ${params.status}`;
    const scopeInstruction = params.permissionScope?.length
      ? ` Permission scope: ${params.permissionScope.join(", ")}.`
      : "";
    setChatContext({
      mode: "chat",
      operationId: params.id,
      operationLabel: params.title,
      operationKind: params.kind,
      operationRequestedAt: new Date(params.requestedAt).getTime(),
      sourceWorkspaceId: params.workspaceId,
      chatOperationInstructions: `Continue the recorded request ${params.id} (${params.kind}) from its current status: ${params.status}.${scopeInstruction} Keep its approval boundary and permission scope unchanged.`,
    });
    setChatComposerDraft(
      chatComposerDraft.trim().length > 0
        ? `${chatComposerDraft.trim()}\n\n${contextLine}`
        : contextLine,
    );
    openChat();
  }

  return (
    <DashboardContainer className="py-8 space-y-6 max-w-6xl px-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        {/* The eyebrow pill above this heading printed `requestsCopy.title`
            too, so the page opened with its own name twice in a row, one line
            apart, with nothing to tell the two apart. The pill carried no
            information the <h1> underneath does not, so the title is stated
            once — as the heading. */}
        <div className="max-w-2xl space-y-2">
          <h1 data-route-heading="agent" className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            {requestsCopy.title}
          </h1>
          <p className="text-sm text-muted-foreground">{requestsCopy.subtitle}</p>
        </div>
        <button
          type="button"
          aria-label={`${requestsCopy.openChat}: ${requestsCopy.title}`}
          onClick={openRequestsChat}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-border bg-foreground px-4 text-xs font-semibold text-background shadow-sm transition-opacity hover:opacity-90"
        >
          <ClipboardList className="h-4 w-4" />
          {requestsCopy.openChat}
        </button>
      </div>

      <section
        data-testid="requests-inline-approval-boundary"
        className="rounded-lg border border-border bg-card px-4 py-3"
      >
        <div className="flex items-start gap-3">
          <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              {inlineApprovalCopy.title}
            </h2>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              {inlineApprovalCopy.body}
            </p>
          </div>
        </div>
      </section>

      {/* Measured 2026-07-30 at 1440x900: these two explanatory blocks took
          28.1% of the first screen between them (54.9% at 390 px) and held no
          interactive element at all, so the actual request list started below
          them every visit. Folded into one remembered disclosure — the text is
          unchanged and one keypress away. */}
      <GuidanceDisclosure
        id="requests-overview"
        title={requestsCopy.howItWorks}
        summary={`${rows
          .map((row) => requestsCopy.rows[row.key].title)
          .join(" · ")}${requestsCopy.stepsSuffix}`}
        testId="studio-requests-overview"
      >
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {rows.map((row) => {
            const Icon = row.icon;
            return (
              <section
                key={row.key}
                className="rounded-2xl border border-border/80 bg-card p-5 shadow-sm"
              >
                <div
                  className={`mb-4 flex h-10 w-10 items-center justify-center rounded-xl ${row.tone}`}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="text-sm font-semibold text-foreground">
                  {requestsCopy.rows[row.key].title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  {requestsCopy.rows[row.key].body}
                </p>
              </section>
            );
          })}
        </div>

        <section
          className="mt-4 grid gap-3 rounded-lg border border-border bg-card p-4 md:grid-cols-3"
          aria-label={strings.a11y.requestReviewFlow}
        >
          {[
            [
              "1",
              flowCopy.prepared.title,
              flowCopy.prepared.body,
              "border border-border bg-muted text-foreground",
            ],
            [
              "2",
              flowCopy.attached.title,
              flowCopy.attached.body,
              "border border-border bg-muted text-foreground",
            ],
            [
              "3",
              flowCopy.approval.title,
              flowCopy.approval.body,
              "border border-border bg-muted text-foreground",
            ],
          ].map(([step, title, body, tone]) => (
            <div
              key={step}
              className="rounded-md border border-border bg-background p-3"
            >
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${tone}`}
              >
                {step}
              </span>
              <h3 className="mt-3 text-sm font-semibold text-foreground">
                {title}
              </h3>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {body}
              </p>
            </div>
          ))}
        </section>
      </GuidanceDisclosure>

      <section className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              {requestsCopy.current.title}
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              {requestsCopy.current.body}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigateTo("import")}
              className="h-10 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              {requestsCopy.actions.addSource}
            </button>
            <button
              type="button"
              onClick={() => navigateTo("library")}
              className="h-10 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              {requestsCopy.actions.library}
            </button>
            <button
              type="button"
              onClick={() => navigateTo("apps-live")}
              className="h-10 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              {requestsCopy.actions.share}
            </button>
            <button
              type="button"
              onClick={() => setRegistryVersion((value) => value + 1)}
              className="inline-flex h-10 items-center gap-2 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              <RefreshCw className="h-4 w-4" />
              {requestsCopy.actions.refresh}
            </button>
          </div>
        </div>

        {/*
          A fourth tile used to sit here reading "Shared access: Limited" — a
          literal beside three measured counts, with nothing behind it. Removed
          rather than left to read as a measurement.
        */}
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-md border border-border bg-background p-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {requestsCopy.tiles.updates}
            </div>
            <div className="mt-1 text-2xl font-semibold text-foreground">
              {requestUpdates.length}
            </div>
          </div>
          <div className="rounded-md border border-border bg-background p-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {requestsCopy.tiles.checks}
            </div>
            <div className="mt-1 text-2xl font-semibold text-foreground">
              {recentRequests.length}
            </div>
          </div>
          <div className="rounded-md border border-border bg-background p-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {requestsCopy.tiles.needsApproval}
            </div>
            <div className="mt-1 text-2xl font-semibold text-foreground">
              {approvalRequests.length}
            </div>
          </div>
        </div>
      </section>

      <div className="space-y-4">
        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold text-foreground">
            {requestsCopy.timeline.title}
          </h2>
          <div className="mt-4 space-y-3">
            {requestUpdates.map((log) => {
              const status = statusLabel(log.stage, requestsCopy);
              return (
                <TimelineItemShell
                  key={log.id}
                  title={log.title}
                  status={status}
                  timestamp={log.timestamp}
                >
                  <div className="grid gap-2">
                    {log.entries.map((entry, index) => (
                      <div
                        key={`${log.id}-${entry.label}-${index}`}
                        className="flex items-start justify-between gap-3 text-xs"
                      >
                        <span className="font-medium text-foreground">
                          {entryLabel(entry.label, requestsCopy)}
                        </span>
                        <span
                          className={
                            entry.tone === "warning" ||
                            entry.tone === "success"
                              ? "text-foreground"
                              : "text-muted-foreground"
                          }
                        >
                          {entry.value}
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      openChatWithContext({
                        id: log.id,
                        title: log.title,
                        status,
                        kind: "agent-worklog",
                        requestedAt: log.timestamp,
                      })
                    }
                    className="mt-3 inline-flex h-9 items-center gap-2 rounded-md border border-border px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
                  >
                    <MessageSquareText className="h-4 w-4" />
                    {requestsCopy.openChat}
                  </button>
                </TimelineItemShell>
              );
            })}
            {visibleRequests.map((run) => {
              const approvalNeeded = requiresApproval(run);
              const title = requestTitle(run.toolName, requestsCopy);
              const status = approvalNeeded
                ? requestsCopy.tiles.needsApproval
                : statusLabel(run.status, requestsCopy);
              const blockingDetail =
                "error" in run && typeof run.error === "string"
                  ? run.error
                  : null;

              return (
                <TimelineItemShell
                  key={run.id}
                  title={title}
                  status={status}
                  timestamp={run.createdAt}
                >
                  {approvalNeeded && (
                    <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                      <div>
                        <div className="text-xs font-semibold text-foreground">
                          {requestsCopy.approval.title}
                        </div>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">
                          {requestsCopy.approval.waiting}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <section>
                      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {requestsCopy.rows.accessControl.title}
                      </h3>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {run.permissionScope.map((scope) => (
                          <span
                            key={`${run.id}-${scope}`}
                            className="rounded-full border border-border bg-card px-2 py-0.5 text-[11px] text-muted-foreground"
                          >
                            {scopeLabel(scope, requestsCopy)}
                          </span>
                        ))}
                      </div>
                    </section>
                    <section>
                      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {requestsCopy.rows.reviewTrail.title}
                      </h3>
                      <dl className="mt-2 grid gap-1 text-xs">
                        <div className="flex items-start justify-between gap-3">
                          <dt className="text-muted-foreground">
                            {requestsCopy.status.recorded}
                          </dt>
                          <dd className="text-right font-medium text-foreground">
                            {title}
                          </dd>
                        </div>
                        {typeof run.durationMs === "number" && (
                          <div className="flex items-start justify-between gap-3">
                            <dt className="text-muted-foreground">Duration</dt>
                            <dd className="font-medium text-foreground">
                              {run.durationMs} ms
                            </dd>
                          </div>
                        )}
                      </dl>
                    </section>
                  </div>

                  {blockingDetail && (
                    <p className="mt-3 rounded-md border border-border bg-background px-3 py-2 text-xs leading-5 text-muted-foreground">
                      {blockingDetail}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() =>
                      openChatWithContext({
                        id: run.id,
                        title,
                        status,
                        kind: run.toolName,
                        requestedAt: run.createdAt,
                        workspaceId: run.workspaceId,
                        permissionScope: run.permissionScope,
                      })
                    }
                    className="mt-3 inline-flex h-9 items-center gap-2 rounded-md border border-border px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
                  >
                    <MessageSquareText className="h-4 w-4" />
                    {requestsCopy.openChat}
                  </button>
                </TimelineItemShell>
              );
            })}
            {!hasRequests && (
              <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6">
                <h3 className="text-sm font-semibold text-foreground">
                  {requestsCopy.timeline.emptyTitle}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {requestsCopy.timeline.emptyBody}
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold text-foreground">
            {requestsCopy.prepare.title}
          </h2>
          <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2 lg:grid-cols-4">
            <span className="rounded-md bg-background px-3 py-2">
              {requestsCopy.prepare.appDraft}
            </span>
            <span className="rounded-md bg-background px-3 py-2">
              {requestsCopy.prepare.sourceImport}
            </span>
            <span className="rounded-md bg-background px-3 py-2">
              {requestsCopy.prepare.reviewCheck}
            </span>
            <span className="rounded-md bg-background px-3 py-2">
              {requestsCopy.prepare.launchSetup}
            </span>
          </div>
        </section>
      </div>
    </DashboardContainer>
  );
}
